import { accessState, accessUnavailable } from "@/lib/effective-access";
import { AccessVerificationRetry } from "@/components/access-verification-retry";
import { useOnboardingStatus } from "@/lib/use-onboarding-status";
import { OnboardingAuthenticationError } from "@/lib/onboarding-submission";
import { getOnboardingSignInPath, getOnboardingCompletionDestination } from "@/lib/auth-redirect";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, lazy, Suspense, type ReactNode } from "react";
import { Switch, Route, Router as WouterRouter, useLocation, useSearch, Redirect, Link } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { startMealReconciliation } from "@/lib/meal-reconciliation";
import { ClerkProvider, SignIn, SignUp, useClerk, useAuth, useUser } from "@clerk/react";
import { useSignIn } from "@clerk/react/legacy";
import { Toaster } from "@/components/ui/toaster";
import { AIBudgetBanner } from "@/components/ai-budget-banner";
import { PrivacyConsentBanner } from "@/components/privacy-consent-banner";
import { ProductAnalyticsListener } from "@/components/product-analytics-listener";
import { ClerkConfigBanner } from "@/components/clerk-config-banner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { AppLayout } from "@/components/layout/app-layout";
import { useGetCurrentUser, useUpdateCurrentUser, getGetCurrentUserQueryKey, useListContentFeed, getListContentFeedQueryKey, useGetCoachAccessStatus, getGetCoachAccessStatusQueryKey } from "@workspace/api-client-react";
import { InstagramEmbed } from "@/components/instagram-embed";
import { ThemeProvider } from "@/lib/theme";
import { GroceryProvider } from "@/lib/grocery-store";
import { LiteModeProvider } from "@/lib/lite-mode";
import { MealPlanProvider } from "@/lib/meal-plan-store";
import { MealPlanProgressBar } from "@/components/meal-plan-progress-bar";
import { PlateTalkProvider } from "@/lib/plate-talk-store";
import { BarcodeScannerProvider } from "@/lib/barcode-scanner-store";
import { AudioControlProvider } from "@/lib/audio-control-store";
import { IntelligenceProvider } from "@/lib/intelligence";
import { BarcodeScannerModal } from "@/components/barcode-scanner";
import {
  getRoleSignInPath,
  getAdminGoogleAccountChoicePath,
  getAdminGoogleRedirectOptions,
  getSignInAudience,
  resolveAdminSignInSession,
  resolveCoachSignInSession,
  resolveHomeRedirect,
  getMemberSignInPath,
  resolveOnboardingAccess,
  resolveProtectedRoute,
  resolveSignInDestination,
  type SignInAudience,
  type UserData,
} from "@/lib/auth-redirect";
import { TourProvider } from "@/lib/product-tour";
import { Loader2, BarChart3, Shield, Brain, Sparkles, Check, Crown, Camera, Droplets, FlaskConical, ShoppingCart, MessageSquare, Dna, HeartPulse, LayoutGrid, ArrowRight } from "lucide-react";
import { PlateOSLogo } from "@/components/plate-os-logo";
import futuristicPlateImg from "@assets/Futuristic_Plate_OS_design_with_glowing_rim_1775943201413.png";
import dnaPillarImg from "@assets/generated_images/help/feature-genetics.png";
import ouraPillarImg from "@assets/generated_images/help/recovery.png";
import deepCommandPillarImg from "@assets/generated_images/help/feature-deep-command.png";
import { ErrorBoundary } from "@/components/error-boundary";
import { Paywall } from "@/components/paywall";
import { changeLanguage } from "@/i18n";
import { setSentryUser } from "@/lib/sentry";
import { clearAccountScopedClientState } from "@/lib/clear-client-state";
import { useWebSignOut } from "@/hooks/use-web-sign-out";
import { WebSignOutRecoveryProvider } from "@/components/web-sign-out-recovery";
import { DelegationProvider } from "@/lib/delegation";
import { RoleSignInChooser } from "@/components/auth/role-sign-in-chooser";
import { AdminAccountSwitchCard } from "@/components/auth/admin-account-switch-card";
import { clerkWebConfig } from "@/lib/clerk-config";
import { IntakeGate } from "@/components/intake-gate";
import { useIntakeStatus } from "@/lib/intake";
import MobileAuth from "@/pages/mobile-auth";
import { NativeShellBridge } from "@/components/native-shell-bridge";

const Dashboard = lazy(() => import("./pages/dashboard"));
const PlateLog = lazy(() => import("./pages/plate-log"));
const Foods = lazy(() => import("./pages/foods"));
const NutritionCore = lazy(() => import("./pages/nutrition-core"));
const Evidence = lazy(() => import("./pages/evidence"));
const Metrics = lazy(() => import("./pages/metrics"));
const Glucose = lazy(() => import("./pages/glucose"));
const Grid = lazy(() => import("./pages/grid"));
const Glossary = lazy(() => import("./pages/glossary"));
const Profile = lazy(() => import("./pages/profile"));
const Grocery = lazy(() => import("./pages/grocery"));
const SmartSwap = lazy(() => import("./pages/smart-swap"));
const FeatureTour = lazy(() => import("./pages/feature-tour"));
const Genetics = lazy(() => import("./pages/genetics"));
const BloodLabs = lazy(() => import("./pages/blood-labs"));
const Snapshots = lazy(() => import("./pages/snapshots"));
const ProgressPhotosPage = lazy(() => import("./pages/progress-photos"));
const HealthDocuments = lazy(() => import("./pages/health-documents"));
const WomensHealth = lazy(() => import("./pages/womens-health"));
const AdminWomensHealthReferences = lazy(() => import("./pages/admin/womens-health-references"));
const Help = lazy(() => import("./pages/help"));
const Companion = lazy(() => import("./pages/companion"));
const Methodology = lazy(() => import("./pages/methodology"));
const Support = lazy(() => import("./pages/support"));
const PlateTalkWorkspace = () => null;
const CoachAi = lazy(() => import("./pages/coach-ai"));
const Onboarding = lazy(() => import("./pages/onboarding"));
const Intake = lazy(() => import("./pages/intake"));
const AccountEssentials = lazy(() => import("./pages/account-essentials"));
const SignInMfaSetup = lazy(() => import("./pages/sign-in-mfa-setup"));
const Billing = lazy(() => import("./pages/billing"));
const AdminBilling = lazy(() => import("./pages/admin/billing"));
const AdminTemporaryPro = lazy(() => import("./pages/admin/temporary-pro"));
const AdminBillingDetail = lazy(() => import("./pages/admin/billing-detail"));
const Subscription = lazy(() => import("./pages/subscription"));
const Integrations = lazy(() => import("./pages/integrations"));
const Oura = lazy(() => import("./pages/oura"));
const Inspiration = lazy(() => import("./pages/inspiration"));
const AdminAgents = lazy(() => import("./pages/admin/agents"));
const AdminSystem = lazy(() => import("./pages/admin/system"));
const AdminProductAnalytics = lazy(() => import("./pages/admin/product-analytics"));
const AdminBuildInfo = lazy(() => import("./pages/admin/build-info"));
const AdminOverview = lazy(() => import("./pages/admin/overview"));
const AdminCommandCenter = lazy(() => import("./pages/admin/command-center"));
const AdminCases = lazy(() => import("./pages/admin/cases"));
const AdminCaseDetail = lazy(() => import("./pages/admin/case-detail"));
const AdminUsers = lazy(() => import("./pages/admin/users"));
const AdminStaff = lazy(() => import("./pages/admin/staff"));
const AdminCtoReports = lazy(() => import("./pages/admin/cto-reports"));
const AdminHealthReports = lazy(() => import("./pages/admin/health-reports"));
const AdminAudience = lazy(() => import("./pages/admin/audience"));
const AdminAudienceDetail = lazy(() => import("./pages/admin/audience-detail"));
const AdminAudienceConnections = lazy(() => import("./pages/admin/audience-connections"));
const AdminAudienceCampaigns = lazy(() => import("./pages/admin/audience-campaigns"));
const AdminUserDetail = lazy(() => import("./pages/admin/user-detail"));
const AdminClientRedirect = lazy(() => import("./pages/admin/client-redirect"));
const ReviewGeneticCatalog = lazy(() => import("./pages/review/genetic-catalog"));
const ReviewQualifications = lazy(() => import("./pages/review/qualifications"));
const GeneticReviewerSignIn = lazy(() => import("./pages/review/sign-in"));

const AdminAudit = lazy(() => import("./pages/admin/audit"));
const CoachSignIn = lazy(() => import("./pages/coach/sign-in"));
const CoachDashboardPage = lazy(() => import("./pages/coach/dashboard"));

function ProtectedCoachRoute({ component: Component }: { component: React.LazyExoticComponent<React.ComponentType> | React.ComponentType }) {
  const { isSignedIn, isLoaded, userId } = useAuth();

  const { data: user, isLoading: isUserLoading, isFetching: isUserFetching, isError: isUserError } = useGetCurrentUser({
    query: {
      enabled: isLoaded && !!isSignedIn,
      retry: 1,
      queryKey: getGetCurrentUserQueryKey(),
      staleTime: 0,
      refetchOnMount: "always",
    },
  });

  const isCoachRole = user?.role === "coach";

  const {
    data: accessStatus,
    isLoading: isAccessLoading,
    isFetching: isAccessFetching,
    isError: isAccessError,
  } = useGetCoachAccessStatus({
    query: {
      enabled: isLoaded && !!isSignedIn && isCoachRole,
      retry: 1,
      queryKey: getGetCoachAccessStatusQueryKey(),
      staleTime: 0,
      refetchOnMount: "always",
    },
  });

  const decision = resolveCoachSignInSession(
    "coach",
    { isLoaded, isSignedIn: !!isSignedIn },
    { user: (user ?? null) as UserData | null, isLoading: isUserLoading || isUserFetching, isError: isUserError },
    { status: accessStatus, isLoading: isAccessLoading || isAccessFetching, isError: isAccessError }
  );

  useEffect(() => {
    setSentryUser(userId ?? null);
    return () => setSentryUser(null);
  }, [userId]);

  if (decision.action === "loading") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#050508] text-blue-400">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin" />
          <span className="text-sm font-medium text-white/60">Loading Coach Workspace...</span>
        </div>
      </div>
    );
  }

  if (decision.action === "redirect" && decision.to !== "/coach/dashboard") {
    return <Redirect to={decision.to} />;
  }

  if (decision.action === "render-sign-in" || decision.action === "wrong-role" || decision.action === "blocked") {
    // Redirect to the coach sign in page to handle these states
    return <Redirect to="/coach/sign-in" />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}
const AdminAccessAudit = lazy(() => import("./pages/admin/access-audit"));
import { SignupAcceptanceGate } from "@/components/legal/signup-acceptance-gate";
const PrivacyPage = lazy(() => import("./pages/legal/privacy"));
const HealthDataPage = lazy(() => import("./pages/legal/health-data"));
const TermsPage = lazy(() => import("./pages/legal/terms"));
const DisclaimerPage = lazy(() => import("./pages/legal/disclaimer"));
const RestoreAccount = lazy(() => import("./pages/restore-account"));
const UiTest = import.meta.env.DEV
  ? lazy(() => import("./pages/ui-test"))
  : null;

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

function currentLocalDestination(location: string): string {
  const pathname = location.split(/[?#]/, 1)[0] || "/";
  return `${pathname}${window.location.search}${window.location.hash}`;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex h-full w-full items-center justify-center min-h-[200px]">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute({
  component: Component,
  requiresSubscription = false,
  allowWithoutIntake = false,
}: {
  component: React.LazyExoticComponent<React.ComponentType> | React.ComponentType;
  requiresSubscription?: boolean;
  allowWithoutIntake?: boolean;
}) {
  const { isSignedIn, isLoaded, userId } = useAuth();
  const [location] = useLocation();
  const queryClient = useQueryClient();
  const { data: user, isLoading, isError, error } = useGetCurrentUser({
    query: { enabled: isLoaded && !!isSignedIn, retry: 1, queryKey: getGetCurrentUserQueryKey(), staleTime: 0, refetchOnMount: "always", refetchInterval: 60_000 },
  });
  const updateUser = useUpdateCurrentUser();
  const tzBackfilled = useRef(false);
  const lastSyncedLangUserId = useRef<string | null>(null);
  useEffect(() => {
    if (user && !user.timezone && !tzBackfilled.current) {
      tzBackfilled.current = true;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && tz !== "UTC") {
        updateUser.mutate({ data: { timezone: tz } }, {
          onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() }),
        });
      }
    }
  }, [user]);

  useEffect(() => {
    if (user && user.language) {
      const userKey = user.email;
      if (lastSyncedLangUserId.current !== userKey) {
        lastSyncedLangUserId.current = userKey;
        changeLanguage(user.language);
      }
    }
  }, [user]);

  useEffect(() => {
    setSentryUser(userId ?? null);
    return () => setSentryUser(null);
  }, [userId]);

  const decision = resolveProtectedRoute(
    { isLoaded, isSignedIn: !!isSignedIn },
    { user: (user ?? null) as UserData | null, isLoading, isError, errorStatus: (error as any)?.status },
    currentLocalDestination(location),
  );

  if (decision.action === "loading") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background text-primary">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin" />
          <span className="text-sm font-medium text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (decision.action === "redirect") {
    return <Redirect to={decision.to} />;
  }

  if (decision.action === "error") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background text-primary">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">Something went wrong loading your profile.</p>
          <button onClick={() => window.location.reload()} className="text-sm text-primary underline">Try again</button>
        </div>
      </div>
    );
  }

  // resolveProtectedRoute only renders when both values are present at
  // runtime; keep the guard explicit for Clerk's nullable TypeScript types.
  if (!user || !userId) {
    return <PageLoader />;
  }

  const isProUser = accessState(user) === "granted";

  // Intake enforcement is account-wide for member product routes. Admin and
  // coach workspaces are independently authorized by their own route shells
  // below; a role label must never bypass this member gate.
  if (!allowWithoutIntake) {
    return (
      <IntakeProtectedContent
        userId={userId}
        user={user as unknown as UserData}
        Component={Component}
        requiresSubscription={requiresSubscription}
      />
    );
  }

  if (requiresSubscription && !isProUser) {
    if (accessUnavailable(user)) return <AccessVerificationRetry />;
    return <Paywall />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

function IntakeProtectedContent({
  userId,
  user,
  Component,
  requiresSubscription,
}: {
  userId: string;
  user: UserData;
  Component: React.LazyExoticComponent<React.ComponentType> | React.ComponentType;
  requiresSubscription: boolean;
}) {
  const [location] = useLocation();
  const status = useIntakeStatus(userId);
  const onRetry = () => {
    void status.refetch();
  };
  const isProUser = accessState(user) === "granted";
  const returnTo = currentLocalDestination(location);

  if (status.isLoading || status.isFetching) {
    return (
      <IntakeGate
        isLoading
        onRetry={onRetry}
        returnTo={returnTo}
      />
    );
  }

  if (status.isError || !status.data) {
    return (
      <IntakeGate
        isError
        isRetrying={status.isFetching}
        onRetry={onRetry}
        returnTo={returnTo}
      />
    );
  }

  if (status.data.required && !status.data.ready) {
    return (
      <IntakeGate
        status={status.data}
        isRetrying={status.isFetching}
        onRetry={onRetry}
        returnTo={returnTo}
      />
    );
  }

  if (requiresSubscription && !isProUser) {
    if (accessUnavailable(user)) return <AccessVerificationRetry />;
    return <Paywall />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

function SignInPage() {
  const [, setLocation] = useLocation();
  const { isLoaded, isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const { handleSignOut: switchAdminAccount, isSigningOut: isSwitchingAccount, signOutError } = useWebSignOut(`${basePath}${getAdminGoogleAccountChoicePath()}`);
  const [destination, setDestination] = useState(() =>
    resolveSignInDestination(window.location.search),
  );
  const activeAudience = getSignInAudience(destination);
  const {
    data: signedInUser,
    isLoading: isSignedInUserLoading,
    isError: isSignedInUserError,
    refetch: refetchSignedInUser,
  } = useGetCurrentUser({
    query: {
      enabled: isLoaded && activeAudience === "admin",
      retry: 1,
      queryKey: getGetCurrentUserQueryKey(),
    },
  });
  const adminSessionDecision = resolveAdminSignInSession(
    activeAudience,
    { isLoaded, isSignedIn: !!isSignedIn },
    {
      user: (signedInUser ?? null) as UserData | null,
      isLoading: isSignedInUserLoading,
      isError: isSignedInUserError,
    },
  );
  const selectAudience = (audience: SignInAudience) => {
    setDestination(resolveSignInDestination(getRoleSignInPath(audience)));
    setLocation(getRoleSignInPath(audience));
  };
  const chooseGoogleAccount = async () => {
    if (!isSignInLoaded || !signIn) return;
    await signIn.authenticateWithRedirect(getAdminGoogleRedirectOptions(basePath));
  };
  const explicitlyChoosingGoogle =
    activeAudience === "admin" &&
    !isSignedIn &&
    new URLSearchParams(window.location.search).get("choose_google") === "1";

  if (adminSessionDecision.action === "redirect") {
    return <Redirect to={adminSessionDecision.to} />;
  }

  return (
    <div className="min-h-screen w-full bg-[#050508] relative overflow-x-hidden overflow-y-auto flex flex-col">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_60%_40%_at_50%_0%,hsl(180_80%_40%/0.06),transparent)]" />
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[hsl(180_80%_50%/0.03)] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle, hsl(180 60% 50%) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
      </div>

      <div className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-5">
        <div className="flex items-center gap-2.5">
          <PlateOSLogo size={32} />
          <span className="text-lg font-bold tracking-[0.08em] text-white/90 uppercase">Plate OS</span>
        </div>
        {import.meta.env.DEV && (
          <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full bg-yellow-500/10 text-yellow-400/80 border border-yellow-500/20">
            Dev
          </span>
        )}
      </div>

      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-16 px-6 lg:px-12 pb-8">
        <div className="hidden lg:flex flex-col items-center flex-1 max-w-lg">
          <div className="relative w-full max-w-[420px] aspect-square">
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,hsl(30_80%_50%/0.08)_0%,hsl(180_80%_50%/0.04)_40%,transparent_70%)]" />
            <img
              src={futuristicPlateImg}
              alt="Plate OS"
              className="w-full h-full object-contain drop-shadow-[0_0_60px_hsl(180_80%_40%/0.15)]"
              draggable={false}
            />
          </div>

          <div className="mt-6 text-center">
            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-white">
              Nutrition Intelligence
            </h1>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-primary mt-1">
              Command Center
            </h2>
            <p className="text-sm text-white/40 mt-3 max-w-sm mx-auto leading-relaxed">
              One plan from three intelligence feeds — your DNA, your wearable, and every body system. Sign in to pick up where you left off.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8 w-full max-w-md">
            {[
              { icon: Dna, label: "DNA", desc: "Nutrigenomics insights" },
              { icon: HeartPulse, label: "Oura", desc: "Recovery & readiness" },
              { icon: LayoutGrid, label: "Deep Command", desc: "10 body systems" },
            ].map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] backdrop-blur-sm">
                <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <f.icon className="w-4 h-4 text-primary" />
                </div>
                <div className="text-center">
                  <div className="text-[11px] font-semibold text-white/80">{f.label}</div>
                  <div className="text-[10px] text-white/30">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:hidden flex flex-col items-center mb-4">
          <div className="relative w-48 h-48 mb-3">
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,hsl(30_80%_50%/0.08)_0%,transparent_70%)]" />
            <img src={futuristicPlateImg} alt="Plate OS" className="w-full h-full object-contain drop-shadow-[0_0_40px_hsl(180_80%_40%/0.12)]" draggable={false} />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-white text-center">
            Nutrition Intelligence
          </h1>
          <h2 className="text-xl font-bold tracking-tight text-primary">Command Center</h2>
        </div>

        <div className="flex-shrink-0 w-full max-w-[560px]">
          <div className="mb-3 px-1 text-center lg:text-left">
            <p className="text-sm font-semibold text-white/85">Choose where you’re signing in</p>
            <p className="mt-0.5 text-xs text-white/40">
              Your account permissions and access checks still apply.
            </p>
          </div>
          <RoleSignInChooser activeAudience={activeAudience} onSelect={selectAudience} />
          <div className="mx-auto w-full max-w-[420px]">
            {adminSessionDecision.action === "loading" ? (
              <div className="flex min-h-64 items-center justify-center rounded-2xl border border-white/[0.06] bg-[#0a0a12]">
                <Loader2 aria-label="Checking administrator access" className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : adminSessionDecision.action === "error" ? (
              <div className="space-y-4 rounded-2xl border border-amber-400/20 bg-[#0a0a12] p-6 text-center">
                <Shield className="mx-auto h-9 w-9 text-amber-400" />
                <h2 className="text-lg font-semibold text-white">Unable to verify administrator access</h2>
                <p className="text-sm text-white/50">
                  Plate OS could not load the current account. This is not an administrator-role denial.
                </p>
                {clerkUser?.primaryEmailAddress?.emailAddress && (
                  <p className="text-sm text-white/70">
                    Signed in as {clerkUser.primaryEmailAddress.emailAddress}
                  </p>
                )}
                <button
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black"
                  onClick={() => void refetchSignedInUser()}
                  type="button"
                >
                  Try again
                </button>
              </div>
            ) : adminSessionDecision.action === "switch-account" ? (
              <>
                <AdminAccountSwitchCard
                  email={typeof signedInUser?.email === "string" ? signedInUser.email : undefined}
                  isSwitching={isSwitchingAccount}
                  onSwitch={switchAdminAccount}
                />
                {signOutError && <p role="alert" className="text-sm text-destructive">Unable to sign out. Please try again.</p>}
              </>
            ) : explicitlyChoosingGoogle ? (
              <div className="space-y-4 rounded-2xl border border-white/[0.06] bg-[#0a0a12] p-6 text-center">
                <Shield className="mx-auto h-9 w-9 text-primary" />
                <h2 className="text-lg font-semibold text-white">Choose your administrator Google account</h2>
                <p className="text-sm text-white/50">
                  Google will ask you to select an account instead of silently restoring the previous session.
                </p>
                <button
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
                  disabled={!isSignInLoaded}
                  onClick={() => void chooseGoogleAccount()}
                  type="button"
                >
                  Choose Google account
                </button>
              </div>
            ) : (
              <SignIn
                routing="path"
                path={`${basePath}/sign-in`}
                signUpUrl={`${basePath}/sign-up?redirect_url=${encodeURIComponent(destination)}`}
                forceRedirectUrl={`${basePath}${destination}`}
                appearance={{
                  variables: {
                    colorBackground: "#0a0a12",
                    colorPrimary: "hsl(174, 72%, 46%)",
                    colorForeground: "rgba(255,255,255,0.88)",
                    colorMutedForeground: "rgba(255,255,255,0.45)",
                    colorInput: "rgba(255,255,255,0.04)",
                    colorInputForeground: "rgba(255,255,255,0.9)",
                    borderRadius: "0.75rem",
                    colorDanger: "#ef4444",
                    colorNeutral: "rgba(255,255,255,0.5)",
                  },
                  elements: {
                    rootBox: "w-full",
                    card: "!bg-[#0a0a12] !border !border-white/[0.06] shadow-[0_0_80px_hsl(180_60%_40%/0.06)] !rounded-2xl",
                    headerTitle: "!text-white",
                    headerSubtitle: "!text-white/50",
                    formFieldLabel: "!text-white/70",
                    formFieldInput: "!bg-white/[0.04] !border-white/[0.08] !text-white",
                    formButtonPrimary: "!bg-[hsl(174,72%,46%)] hover:!bg-[hsl(174,72%,40%)] !text-black !font-semibold",
                    footerActionLink: "!text-[hsl(174,72%,46%)]",
                    socialButtonsBlockButton: "!bg-white/[0.04] !border-white/[0.08] !text-white hover:!bg-white/[0.08]",
                    socialButtonsBlockButtonText: "!text-white/80",
                    dividerLine: "!bg-white/[0.06]",
                    dividerText: "!text-white/30",
                    formFieldInputShowPasswordButton: "!text-white/40",
                    identityPreview: "!bg-white/[0.04] !border-white/[0.08]",
                    identityPreviewText: "!text-white/70",
                    identityPreviewEditButton: "!text-[hsl(174,72%,46%)]",
                    otpCodeFieldInput: "!bg-white/[0.04] !border-white/[0.08] !text-white",
                    formResendCodeLink: "!text-[hsl(174,72%,46%)]",
                  },
                }}
              />
            )}
          </div>
        </div>
      </div>

      <AuthMarketingSection />
    </div>
  );
}

function SignUpPage() {
  const destination = resolveSignInDestination(window.location.search);
  const signInPath = getMemberSignInPath(destination);
  return (
    <div className="min-h-screen w-full bg-[#050508] relative overflow-x-hidden overflow-y-auto flex flex-col">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_60%_40%_at_50%_0%,hsl(180_80%_40%/0.06),transparent)]" />
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[hsl(180_80%_50%/0.03)] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle, hsl(180 60% 50%) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
      </div>

      <div className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-5">
        <div className="flex items-center gap-2.5">
          <PlateOSLogo size={32} />
          <span className="text-lg font-bold tracking-[0.08em] text-white/90 uppercase">Plate OS</span>
        </div>
      </div>

      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-16 px-6 lg:px-12 pb-8">
        <div className="hidden lg:flex flex-col items-center flex-1 max-w-lg">
          <div className="relative w-full max-w-[360px] aspect-square">
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,hsl(30_80%_50%/0.08)_0%,hsl(180_80%_50%/0.04)_40%,transparent_70%)]" />
            <img
              src={futuristicPlateImg}
              alt="Plate OS"
              className="w-full h-full object-contain drop-shadow-[0_0_60px_hsl(180_80%_40%/0.15)]"
              draggable={false}
            />
          </div>

          <div className="mt-6 text-center">
            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-white">
              Start Tracking Your
            </h1>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-primary mt-1">
              Nutrition Today
            </h2>
            <p className="text-sm text-white/40 mt-3 max-w-sm mx-auto leading-relaxed">
              Create your account to access AI food analysis, 157+ nutrient tracking, and personalized health insights.
            </p>
          </div>
        </div>

        <div className="lg:hidden flex flex-col items-center mb-4">
          <div className="relative w-40 h-40 mb-3">
            <img src={futuristicPlateImg} alt="Plate OS" className="w-full h-full object-contain drop-shadow-[0_0_40px_hsl(180_80%_40%/0.12)]" draggable={false} />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-white text-center">Start Tracking Your</h1>
          <h2 className="text-xl font-bold tracking-tight text-primary">Nutrition Today</h2>
        </div>

        <div className="flex-shrink-0 w-full max-w-[420px]">
          <SignupAcceptanceGate>
          <SignUp
            routing="path"
            path={`${basePath}/sign-up`}
            signInUrl={`${basePath}${signInPath}`}
            forceRedirectUrl={`${basePath}${destination}`}
            appearance={{
              variables: {
                colorBackground: "#0a0a12",
                colorPrimary: "hsl(174, 72%, 46%)",
                colorForeground: "rgba(255,255,255,0.88)",
                colorMutedForeground: "rgba(255,255,255,0.45)",
                colorInput: "rgba(255,255,255,0.04)",
                colorInputForeground: "rgba(255,255,255,0.9)",
                borderRadius: "0.75rem",
                colorDanger: "#ef4444",
                colorNeutral: "rgba(255,255,255,0.5)",
              },
              elements: {
                rootBox: "w-full",
                card: "!bg-[#0a0a12] !border !border-white/[0.06] shadow-[0_0_80px_hsl(180_60%_40%/0.06)] !rounded-2xl",
                headerTitle: "!text-white",
                headerSubtitle: "!text-white/50",
                formFieldLabel: "!text-white/70",
                formFieldInput: "!bg-white/[0.04] !border-white/[0.08] !text-white",
                formButtonPrimary: "!bg-[hsl(174,72%,46%)] hover:!bg-[hsl(174,72%,40%)] !text-black !font-semibold",
                footerActionLink: "!text-[hsl(174,72%,46%)]",
                socialButtonsBlockButton: "!bg-white/[0.04] !border-white/[0.08] !text-white hover:!bg-white/[0.08]",
                socialButtonsBlockButtonText: "!text-white/80",
                dividerLine: "!bg-white/[0.06]",
                dividerText: "!text-white/30",
              },
            }}
          />
          </SignupAcceptanceGate>
        </div>
      </div>

      <AuthMarketingSection />
    </div>
  );
}

function LandingInspiration() {
  const { data } = useListContentFeed(
    { placement: "landing" },
    {
      query: {
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        queryKey: getListContentFeedQueryKey({ placement: "landing" }),
      },
    },
  );

  const videos = (data ?? []).slice(0, 3);
  if (videos.length === 0) return null;

  return (
    <div className="relative max-w-4xl mx-auto mb-16 lg:mb-20">
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold tracking-wider uppercase mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          From The Feed
        </span>
        <h3 className="text-2xl lg:text-3xl font-bold tracking-tight text-white">
          Real Food, <span className="text-primary">Real Results</span>
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {videos.map((v) => (
          <InstagramEmbed key={v.id} url={v.url} mediaType={v.mediaType} title={v.title} />
        ))}
      </div>
    </div>
  );
}

function AuthCtaButtons({ align = "start" }: { align?: "start" | "center" }) {
  return (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-3 ${align === "center" ? "justify-center" : ""}`}>
      <Link
        href="/sign-up"
        className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[hsl(174,72%,46%)] hover:bg-[hsl(174,72%,40%)] text-black text-sm font-semibold transition-colors shadow-[0_0_40px_hsl(174_72%_46%/0.25)]"
      >
        Create your free account
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </Link>
      <Link
        href="/sign-in"
        className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white/70 hover:text-white hover:border-primary/30 text-sm font-medium transition-colors"
      >
        I already have an account
      </Link>
    </div>
  );
}

type Pillar = {
  index: string;
  icon: typeof Dna;
  eyebrow: string;
  title: string;
  highlight: string;
  desc: string;
  bullets: string[];
  img: string;
  alt: string;
  caption: string;
  reverse?: boolean;
};

function PillarSection({ pillar }: { pillar: Pillar }) {
  const { icon: Icon, index, eyebrow, title, highlight, desc, bullets, img, alt, caption, reverse } = pillar;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 lg:gap-14">
      <div className={reverse ? "lg:order-2" : ""}>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 border border-primary/20">
            <Icon className="w-4.5 h-4.5 text-primary" />
          </div>
          <span className="text-[11px] font-mono tracking-[0.18em] uppercase text-primary/70">{eyebrow}</span>
          <span className="ml-auto text-[11px] font-mono text-white/20">{index}</span>
        </div>
        <h3 className="text-2xl lg:text-3xl font-bold tracking-tight text-white mb-3">
          {title} <span className="text-primary">{highlight}</span>
        </h3>
        <p className="text-sm lg:text-base text-white/45 leading-relaxed mb-5 max-w-xl">{desc}</p>
        <ul className="space-y-2.5">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5">
              <div className="mt-0.5 w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Check className="w-2.5 h-2.5 text-primary" />
              </div>
              <span className="text-sm text-white/65 leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={reverse ? "lg:order-1" : ""}>
        <div className="relative">
          <div className="absolute -inset-6 bg-[radial-gradient(ellipse_at_center,hsl(180_70%_45%/0.07),transparent_70%)] rounded-3xl" />
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.07] bg-[#0a0a12] shadow-[0_0_80px_hsl(180_60%_40%/0.07)]">
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/[0.05] bg-white/[0.02]">
              <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <span className="ml-2 text-[10px] font-mono tracking-wider uppercase text-white/30">{caption}</span>
            </div>
            <img src={img} alt={alt} className="w-full h-auto" draggable={false} loading="lazy" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AuthMarketingSection() {
  const PILLARS: Pillar[] = [
    {
      index: "01 / 03",
      icon: Dna,
      eyebrow: "Nutrigenomics",
      title: "Your nutrition,",
      highlight: "decoded from your DNA",
      desc: "Upload a DNA file or answer a short questionnaire. Plate OS reads variants like MTHFR and CYP1A2 and turns them into clear, personalized nutrient priorities.",
      bullets: [
        "Gene → nutrient insights backed by clinical evidence",
        "Risk flags you can actually act on",
        "Priorities flow into Smart Swap, food picks & Plate Talk",
      ],
      img: dnaPillarImg,
      alt: "Plate OS DNA Nutrition — variant insights and personalized nutrient guidance",
      caption: "DNA Nutrition",
    },
    {
      index: "02 / 03",
      icon: HeartPulse,
      eyebrow: "Oura Powerhouse",
      title: "Recovery, sleep & readiness,",
      highlight: "in the loop",
      desc: "Connect your Oura ring and your wearable becomes part of the plan — sleep, HRV, strain and readiness, briefed by your AI coach every single morning.",
      bullets: [
        "A daily AI morning brief from your wearable",
        "Per-metric tiles: sleep, HRV, strain & readiness",
        "Nutrition that adapts to how recovered you are",
      ],
      img: ouraPillarImg,
      alt: "Plate OS Recovery & Readiness — wearable sleep, HRV and strain tiles",
      caption: "Recovery & Readiness",
      reverse: true,
    },
    {
      index: "03 / 03",
      icon: LayoutGrid,
      eyebrow: "Deep Command",
      title: "Every body system on one",
      highlight: "Command Matrix",
      desc: "The Command Matrix lays out 10 body-system domains — metabolic, immune, gut, neural, cardio and more — each as its own tile, alongside a Neural Rank composite score and tier you can track over time.",
      bullets: [
        "10 body-system domains, each its own tile",
        "Neural Rank composite score + tier",
        "Tap any domain to expand the full breakdown",
      ],
      img: deepCommandPillarImg,
      alt: "Plate OS Command Matrix — 10 body-system domain tiles with Neural Rank composite score",
      caption: "Command Matrix",
    },
  ];

  const FEATURES = [
    { icon: Camera, title: "Snap & Analyze", desc: "Photograph any meal — AI identifies foods and calculates 157+ nutrients in seconds" },
    { icon: MessageSquare, title: "Plate Talk AI", desc: "Chat with your nutrition AI by text, photo or voice for plans and answers" },
    { icon: FlaskConical, title: "Evidence Lab", desc: "Science-backed nutrient analysis with clinical links and microbiome scoring" },
    { icon: ShoppingCart, title: "AI Grocery Lists", desc: "Auto-built shopping lists from your nutrient gaps and health goals" },
    { icon: Droplets, title: "Hydration Intelligence", desc: "Smart hydration tracking with electrolyte balance and daily targets" },
    { icon: Brain, title: "Dual-AI Engine", desc: "GPT-4.1 for food analysis, Gemini for conversational coaching" },
    { icon: BarChart3, title: "Metric Explorer", desc: "Trend every nutrient across all body systems day by day" },
    { icon: Shield, title: "Private by design", desc: "Your DNA and health data stay yours — delete it any time" },
  ];

  const PRO_PERKS = [
    "AI food analysis",
    "DNA nutrigenomics tools",
    "Oura / wearable integration",
    "157+ nutrient tracking",
    "10 body-system Command Matrix",
    "Personalized meal plans",
    "Plate Talk conversations",
    "Priority support",
  ];

  return (
    <div className="relative z-10">
      <div className="border-t border-white/[0.04]" />

      <div className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(180_80%_40%/0.02)] to-transparent pointer-events-none" />

        <div className="relative z-10 px-6 lg:px-12 max-w-6xl mx-auto">
          {/* Funnel hero — combined promise + CTA */}
          <div className="text-center mb-16 lg:mb-24">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold tracking-wider uppercase mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              The Nutrition Command Center
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-white mb-4 max-w-3xl mx-auto leading-tight">
              Your DNA, your wearables, and every body system — <span className="text-primary">working as one</span>
            </h2>
            <p className="text-base lg:text-lg text-white/45 max-w-2xl mx-auto leading-relaxed mb-8">
              Plate OS fuses three intelligence feeds into one plan: what your genes need, how recovered you are, and how every body system is trending.
            </p>
            <div className="flex justify-center mb-8">
              <AuthCtaButtons align="center" />
            </div>
            <div className="flex flex-wrap justify-center gap-2.5">
              {PILLARS.map((p) => (
                <span key={p.eyebrow} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.07] text-xs text-white/55">
                  <p.icon className="w-3.5 h-3.5 text-primary" />
                  {p.eyebrow}
                </span>
              ))}
            </div>
          </div>

          {/* Three co-equal pillars, each anchored to a real app screenshot */}
          <div className="space-y-20 lg:space-y-28 mb-20 lg:mb-28">
            {PILLARS.map((p) => (
              <PillarSection key={p.eyebrow} pillar={p} />
            ))}
          </div>

          {/* Supporting features */}
          <div className="mb-16 lg:mb-20">
            <div className="text-center mb-10">
              <h3 className="text-2xl lg:text-3xl font-bold tracking-tight text-white mb-3">
                Everything else, <span className="text-primary">built in</span>
              </h3>
              <p className="text-sm lg:text-base text-white/40 max-w-2xl mx-auto leading-relaxed">
                The three pillars sit on top of a complete nutrition toolkit — logging, coaching, evidence and more.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {FEATURES.map((f) => (
                <div key={f.title} className="group p-4 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-primary/20 hover:bg-white/[0.04] transition-all duration-300">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-3 group-hover:bg-primary/15 transition-colors">
                    <f.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className="text-sm font-semibold text-white/90 mb-1">{f.title}</h4>
                  <p className="text-xs text-white/35 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <LandingInspiration />

          {/* Pro perks */}
          <div className="relative max-w-lg mx-auto mb-16 lg:mb-20">
            <div className="absolute -inset-4 bg-[radial-gradient(ellipse_at_center,hsl(174_72%_46%/0.06),transparent_70%)] rounded-3xl" />
            <div className="relative p-8 rounded-2xl bg-[#0a0a14] border border-primary/15 shadow-[0_0_60px_hsl(174_72%_46%/0.06)]">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="text-sm font-bold text-amber-400 uppercase tracking-wider">Plate OS Pro</span>
              </div>

              <p className="text-xs text-white/30 mb-6">Explore your nutrition intelligence toolkit. Coaching and DNA purchases are separate.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-6">
                {PRO_PERKS.map((perk) => (
                  <div key={perk} className="flex items-center gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                      <Check className="w-2.5 h-2.5 text-primary" />
                    </div>
                    <span className="text-xs text-white/60">{perk}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-white/[0.06]">
                <p className="text-[11px] text-white/25 text-center">
                  Sign in to see your current access and billing options
                </p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="absolute -inset-6 bg-[radial-gradient(ellipse_at_center,hsl(174_72%_46%/0.07),transparent_70%)] rounded-3xl" />
            <div className="relative">
              <h3 className="text-2xl lg:text-4xl font-bold tracking-tight text-white mb-4">
                Ready to command your nutrition?
              </h3>
              <p className="text-sm lg:text-base text-white/45 max-w-xl mx-auto leading-relaxed mb-8">
                Start free in under a minute. Bring your DNA and wearable whenever you're ready.
              </p>
              <div className="flex justify-center">
                <AuthCtaButtons align="center" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-12 py-4 flex items-center justify-between border-t border-white/[0.04]">
        <p className="text-[10px] text-white/20 font-mono tracking-wider uppercase">Plate OS v1.0 — Nutrition Intelligence Platform</p>
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-primary/40" />
          <span className="text-[10px] text-white/20">Powered by AI</span>
        </div>
      </div>
    </div>
  );
}

function HomeRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  const decision = resolveHomeRedirect({ isLoaded, isSignedIn: !!isSignedIn });

  if (decision.action === "loading") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (isLoaded && !isSignedIn) {
    return <Suspense fallback={<PageLoader />}><AcquisitionLanding /></Suspense>;
  }
  if (decision.action === "redirect") {
    return <Redirect to={decision.to} />;
  }

  return <Redirect to="/sign-in" />;
}

function OnboardingRoute() {
  const search = useSearch();
  const { t } = useTranslation();
  const { isSignedIn, isLoaded, userId } = useAuth();
  const status = useOnboardingStatus();
  const decision = resolveOnboardingAccess({ isLoaded, isSignedIn: !!isSignedIn });

  if (decision.action === "loading" || (isSignedIn && status.isPending)) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (decision.action === "redirect") {
    return <Redirect to={getOnboardingSignInPath(search)} />;
  }

  if (status.isError) {
    if (status.error instanceof OnboardingAuthenticationError) {
      return <Redirect to={getOnboardingSignInPath(search)} />;
    }
    return <div role="alert" className="p-8">{t("common.error")}
      <button className="underline ml-2" onClick={() => void status.refetch()}>{t("common.retry")}</button>
    </div>;
  }
  if (status.data?.provisioned && status.data.missingDocTypes.length === 0) {
    return <Redirect to={getOnboardingCompletionDestination(search)} />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Onboarding key={`${userId}:${!!status.data?.provisioned}`} resumeLegal={!!status.data?.provisioned} />
    </Suspense>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let stopReconciliation: (() => void) | undefined;
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== userId) {
        stopReconciliation?.();
        stopReconciliation = undefined;
      }
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClient.clear();
        clearAccountScopedClientState();
      }
      if (userId && !stopReconciliation) {
        stopReconciliation = startMealReconciliation(queryClient);
      }
      prevUserIdRef.current = userId;
    });
    return () => {
      stopReconciliation?.();
      unsubscribe();
    };
  }, [addListener, queryClient]);

  return null;
}

function PersistentPlateLog() {
  const [location] = useLocation();
  const isActive = location === "/plate-log" || location.startsWith("/plate-log?");
  const mountedRef = useRef(false);

  if (isActive) mountedRef.current = true;
  if (!mountedRef.current) return null;

  return (
    <div style={{ display: isActive ? "contents" : "none" }}>
      <ErrorBoundary>
        <ProtectedRoute component={PlateLog} />
      </ErrorBoundary>
    </div>
  );
}

function AppRouteSwitch() {
  return (
    <Switch>
      <Route path="/mobile-auth" component={MobileAuth} />
        <Route path="/" component={HomeRedirect} />
        <Route path="/body-transformation"><Suspense fallback={<PageLoader />}><AcquisitionLanding segment="body" /></Suspense></Route>
        <Route path="/optimize"><Suspense fallback={<PageLoader />}><AcquisitionLanding segment="optimize" /></Suspense></Route>
        <Route path="/assessment"><Suspense fallback={<PageLoader />}><AcquisitionAssessment /></Suspense></Route>
        <Route path="/results"><Redirect to="/assessment" /></Route>
        <Route path="/health-map"><ProtectedRoute component={AcquisitionMap} /></Route>
        <Route path="/settings/privacy"><Suspense fallback={<PageLoader />}><AcquisitionPrivacy /></Suspense></Route>
        <Route path="/signup"><Redirect to="/sign-up" /></Route>
        <Route path="/signin"><Redirect to="/sign-in" /></Route>
        <Route path="/pricing"><Redirect to={getMemberSignInPath("/subscription")} /></Route>
        <Route path="/admin/acquisition"><Suspense fallback={<PageLoader />}><AdminAcquisition /></Suspense></Route>
        <Route path="/sign-in/tasks/setup-mfa"><Suspense fallback={<PageLoader />}><SignInMfaSetup /></Suspense></Route>
        <Route path="/sign-in/*?" component={SignInPage} />
        <Route path="/sign-up/*?" component={SignUpPage} />
        <Route path="/onboarding" component={OnboardingRoute} />
        <Route path="/intake"><ProtectedRoute component={Intake} allowWithoutIntake /></Route>
        <Route path="/account"><ProtectedRoute component={AccountEssentials} allowWithoutIntake /></Route>

        <Route path="/legal/privacy">
          <Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>
        </Route>
        <Route path="/legal/health-data">
          <Suspense fallback={<PageLoader />}><HealthDataPage /></Suspense>
        </Route>
        <Route path="/legal/terms">
          <Suspense fallback={<PageLoader />}><TermsPage /></Suspense>
        </Route>
        <Route path="/legal/disclaimer">
          <Suspense fallback={<PageLoader />}><DisclaimerPage /></Suspense>
        </Route>

        <Route path="/restore-account">
          <Suspense fallback={<PageLoader />}><RestoreAccount /></Suspense>
        </Route>

        <Route path="/mission-control">
          <Redirect to="/admin/agents" />
        </Route>

        <Route path="/admin/command-center">
          <Suspense fallback={<PageLoader />}><AdminCommandCenter /></Suspense>
        </Route>
        <Route path="/admin/cases/incident/:id">
          <Suspense fallback={<PageLoader />}><AdminCaseDetail /></Suspense>
        </Route>
        <Route path="/admin/cases/ticket/:id">
          <Suspense fallback={<PageLoader />}><AdminCaseDetail /></Suspense>
        </Route>
        <Route path="/admin/cases">
          <Suspense fallback={<PageLoader />}><AdminCases /></Suspense>
        </Route>
        <Route path="/admin/womens-health-references">
          <Suspense fallback={<PageLoader />}><AdminWomensHealthReferences /></Suspense>
        </Route>
        <Route path="/admin/billing/temporary-pro">
          <Suspense fallback={<PageLoader />}><AdminTemporaryPro /></Suspense>
        </Route>
        <Route path="/admin/billing/:userId">
          <Suspense fallback={<PageLoader />}><AdminBillingDetail /></Suspense>
        </Route>
        <Route path="/admin/billing">
          <Suspense fallback={<PageLoader />}><AdminBilling /></Suspense>
        </Route>
        <Route path="/admin/client/:clientId">
          <Suspense fallback={<PageLoader />}><AdminClientRedirect /></Suspense>
        </Route>
        <Route path="/admin/users/:id">
          <Suspense fallback={<PageLoader />}><AdminUserDetail /></Suspense>
        </Route>
        <Route path="/admin/users">
          <Suspense fallback={<PageLoader />}><AdminUsers /></Suspense>
        </Route>
        <Route path="/coach/sign-in" component={CoachSignIn} />
        <Route path="/coach/dashboard" component={() => <ProtectedCoachRoute component={CoachDashboardPage} />} />
        <Route path="/admin/audience/connections">
          <Suspense fallback={<PageLoader />}><AdminAudienceConnections /></Suspense>
        </Route>
        <Route path="/admin/audience/campaigns">
          <Suspense fallback={<PageLoader />}><AdminAudienceCampaigns /></Suspense>
        </Route>
        <Route path="/admin/audience/:id">
          <Suspense fallback={<PageLoader />}><AdminAudienceDetail /></Suspense>
        </Route>
        <Route path="/admin/audience">
          <Suspense fallback={<PageLoader />}><AdminAudience /></Suspense>
        </Route>
        <Route path="/admin/cto-reports/:id">
          <Suspense fallback={<PageLoader />}><AdminCtoReports /></Suspense>
        </Route>
        <Route path="/admin/cto-reports">
          <Suspense fallback={<PageLoader />}><AdminCtoReports /></Suspense>
        </Route>
        <Route path="/admin/health-reports/new">
          <Suspense fallback={<PageLoader />}><AdminHealthReports /></Suspense>
        </Route>
        <Route path="/admin/health-reports/:id">
          <Suspense fallback={<PageLoader />}><AdminHealthReports /></Suspense>
        </Route>
        <Route path="/admin/health-reports">
          <Suspense fallback={<PageLoader />}><AdminHealthReports /></Suspense>
        </Route>
        <Route path="/admin/staff">
          <Suspense fallback={<PageLoader />}><AdminStaff /></Suspense>
        </Route>
        <Route path="/admin/audit">
          <Suspense fallback={<PageLoader />}><AdminAudit /></Suspense>
        </Route>
        <Route path="/admin/access-audit">
          <Suspense fallback={<PageLoader />}><AdminAccessAudit /></Suspense>
        </Route>
        <Route path="/admin/agents">
          <Suspense fallback={<PageLoader />}><AdminAgents /></Suspense>
        </Route>
        <Route path="/admin/build-info">
          <Suspense fallback={<PageLoader />}><AdminBuildInfo /></Suspense>
        </Route>
        <Route path="/admin/system">
          <Suspense fallback={<PageLoader />}><AdminSystem /></Suspense>
        </Route>
        <Route path="/admin/analytics">
          <Suspense fallback={<PageLoader />}><AdminProductAnalytics /></Suspense>
        </Route>
        <Route path="/admin">
          <Suspense fallback={<PageLoader />}><AdminOverview /></Suspense>
        </Route>
        <Route path="/review/sign-in/*?">
          <Suspense fallback={<PageLoader />}><GeneticReviewerSignIn /></Suspense>
        </Route>
        <Route path="/review/qualifications">
          <Suspense fallback={<PageLoader />}><ReviewQualifications /></Suspense>
        </Route>
        <Route path="/review/genetic-catalog">
          <Suspense fallback={<PageLoader />}><ReviewGeneticCatalog /></Suspense>
        </Route>

        <Route path="/dashboard"><ProtectedRoute component={Dashboard} /></Route>
        <Route path="/feature-tour"><ProtectedRoute component={FeatureTour} /></Route>
        <Route path="/grocery"><ProtectedRoute component={Grocery} requiresSubscription /></Route>
        <Route path="/plate-log">{() => null}</Route>
        <Route path="/foods"><ProtectedRoute component={Foods} /></Route>
        <Route path="/recipes"><ProtectedRoute component={NutritionCore} /></Route>
        <Route path="/reusable-meals"><ProtectedRoute component={NutritionCore} /></Route>
        <Route path="/nutrition-tools"><ProtectedRoute component={NutritionCore} /></Route>
        <Route path="/nutrition-reports"><ProtectedRoute component={NutritionCore} /></Route>
        <Route path="/nutrition-targets"><ProtectedRoute component={NutritionCore} /></Route>
        <Route path="/evidence"><ProtectedRoute component={Evidence} requiresSubscription /></Route>
        <Route path="/metrics"><ProtectedRoute component={Metrics} requiresSubscription /></Route>
        <Route path="/glucose/meal"><ProtectedRoute component={Glucose} requiresSubscription /></Route>
        <Route path="/glucose"><ProtectedRoute component={Glucose} requiresSubscription /></Route>
        <Route path="/oura"><OuraEntry /></Route>
        <Route path="/grid"><ProtectedRoute component={Grid} requiresSubscription /></Route>
        <Route path="/smart-swap"><ProtectedRoute component={SmartSwap} requiresSubscription /></Route>
        <Route path="/genetics"><ProtectedRoute component={Genetics} requiresSubscription /></Route>
        <Route path="/blood-labs"><ProtectedRoute component={BloodLabs} requiresSubscription /></Route>
        <Route path="/health-documents"><ProtectedRoute component={HealthDocuments} /></Route>
        <Route path="/womens-health"><ProtectedRoute component={WomensHealth} /></Route>
        <Route path="/snapshots"><ProtectedRoute component={Snapshots} requiresSubscription /></Route>
        <Route path="/progress-photos"><ProtectedRoute component={ProgressPhotosPage} /></Route>
        <Route path="/profile"><ProtectedRoute component={Profile} /></Route>
        <Route path="/glossary"><ProtectedRoute component={Glossary} /></Route>
        <Route path="/inspiration"><ProtectedRoute component={Inspiration} /></Route>
        <Route path="/help"><ProtectedRoute component={Help} /></Route>
        <Route path="/companion"><ProtectedRoute component={Companion} /></Route>
        <Route path="/methodology"><ProtectedRoute component={Methodology} /></Route>
        <Route path="/support"><ProtectedRoute component={Support} allowWithoutIntake /></Route>
        <Route path="/plate-talk"><ProtectedRoute component={PlateTalkWorkspace} requiresSubscription /></Route>
        <Route path="/coach-ai"><ProtectedRoute component={CoachAi} requiresSubscription /></Route>
        <Route path="/billing"><ProtectedRoute component={Billing} allowWithoutIntake /></Route>
        <Route path="/subscription"><ProtectedRoute component={Subscription} allowWithoutIntake /></Route>
        <Route path="/settings/integrations"><ProtectedRoute component={Integrations} requiresSubscription /></Route>

        {import.meta.env.DEV && UiTest && (
          <Route path="/ui-test">
            <Suspense fallback={<PageLoader />}>
              <UiTest />
            </Suspense>
          </Route>
        )}

        <Route component={NotFound} />
    </Switch>
  );
}

function OuraEntry() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <PageLoader />;
  return isSignedIn ? <ProtectedRoute component={Oura} requiresSubscription /> : <Suspense fallback={<PageLoader />}><AcquisitionLanding segment="oura" /></Suspense>;
}
function AppRouter() {
  const [location] = useLocation();
  const { isSignedIn } = useAuth();
  const isStandaloneRoute =
    location === "/" ||
    ["/body-transformation", "/optimize", "/assessment", "/results", "/settings/privacy", "/signup", "/signin", "/pricing"].includes(location) ||
    (location === "/oura" && !isSignedIn) ||
    location === "/mobile-auth" ||
    location.startsWith("/sign-in") ||
    location.startsWith("/sign-up") ||
    location === "/onboarding" ||
    location.startsWith("/intake") ||
    location === "/account" ||
    location.startsWith("/legal/") ||
    location === "/restore-account" ||
    location.startsWith("/coach/sign-in") ||
    location.startsWith("/review/") ||
    location === "/ui-test";

  return (
    <>
      {!isStandaloneRoute && <MealPlanProgressBar />}
      {isStandaloneRoute ? (
        <AppRouteSwitch />
      ) : (
        <AppLayout>
          <PersistentPlateLog />
          <AppRouteSwitch />
        </AppLayout>
      )}
    </>
  );
}

/** Account-scoped stores remount per Clerk identity so stale async work cannot cross accounts. */
function OwnedStateProviders({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser();
  const owner = !isLoaded ? "loading" : user?.id ?? "anonymous";
  return (
    <GroceryProvider key={`g-${owner}`} ownerKey={owner}>
      <MealPlanProvider key={`m-${owner}`}>{children}</MealPlanProvider>
    </GroceryProvider>
  );
}

function ClerkProviderWithRoutes() {
  const [location, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkWebConfig.publishableKey}
      proxyUrl={clerkWebConfig.proxyUrl}
      taskUrls={{ "setup-mfa": `${basePath}/sign-in/tasks/setup-mfa` }}
      localization={location === "/coach/sign-in" || location.startsWith("/coach/sign-in/")
        ? { formButtonPrimary: "Sign in as Coach" }
        : undefined}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <NativeShellBridge />
        <ClerkQueryClientCacheInvalidator />
        <LiteModeProvider>
          <WebSignOutRecoveryProvider>
          <OwnedStateProviders>
              <PlateTalkProvider>
                <BarcodeScannerProvider>
                  <AudioControlProvider>
                    <TourProvider>
                      <TooltipProvider>
                        <IntelligenceProvider>
                          <DelegationProvider>
                            <AppRouter />
                            <PrivacyConsentBanner />
                            <ProductAnalyticsListener />
                            <BarcodeScannerModal />
                            <AIBudgetBanner />
                            <Toaster />
                          </DelegationProvider>
                        </IntelligenceProvider>
                      </TooltipProvider>
                    </TourProvider>
                  </AudioControlProvider>
                </BarcodeScannerProvider>
              </PlateTalkProvider>
          </OwnedStateProviders>
          </WebSignOutRecoveryProvider>
        </LiteModeProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ClerkConfigBanner />
        <WouterRouter base={basePath}>
          <ClerkProviderWithRoutes />
        </WouterRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;

const AcquisitionLanding = lazy(() => import("./pages/acquisition/session").then(m => ({ default: m.PublicFunnel })));

const AcquisitionMap = lazy(() => import("./pages/acquisition/health-map"));

const AcquisitionPrivacy = lazy(() => import("./pages/acquisition/privacy"));

const AdminAcquisition = lazy(() => import("./pages/admin/acquisition"));

const AcquisitionAssessment = lazy(() => import("./pages/acquisition/assessment"));
